import { observer } from 'mobx-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';
import classes from './drugReferenceFgNdcToBulkNdcTable.module.scss';
import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import searchIcon from 'assets/icons/PPSIcons/searchIcon.svg';
import { useDrugReferenceFgNdcToBulkNdcTable } from './useDrugReferenceFgNdcToBulkNdc';
import AddRowModalDrugReferenceFgNdcToBulkNdc from './utils/AddRowModalDrugReferenceFgNdcToBulkNdc';
import DeleteConfirmationModalDrugReferenceFgNdcToBulkNdc from './utils/DeleteConfirmationModalDrugReferenceFgNdcToBulkNdc';
import { DrugReferenceFgNdcToBulkNdc } from 'Interfaces/ReferenceInterfaces/DrugReferenceFgNdcToBulkNdcInterface';

interface DrugReferenceFgNdcToBulkNdcTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const DrugReferenceFgNdcToBulkNdcTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: DrugReferenceFgNdcToBulkNdcTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;
    const {
      drugReferenceData,
      isLoading,
      pagination,
      coatingOptions,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      handleSearchChange,
      prepareFormData,
      onPageChange,
      dosageFormOptions,
      handleExportToExcel
    } = useDrugReferenceFgNdcToBulkNdcTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      }
      // {
      //   label: 'Delete Row',
      //   icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
      //   command: () => setShowDeleteModal(true)
      // }
    ];

    const actionBodyTemplate = (rowData: DrugReferenceFgNdcToBulkNdc) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const coatingTemplate = (rowData: DrugReferenceFgNdcToBulkNdc) => {
      return rowData.coating ? rowData.coating.description : '';
    };

    // Format primary ingredient to remove trailing zeroes
    const formatPrimaryIngredient = (
      primaryIngredient: number | string | null | undefined
    ): string => {
      if (
        primaryIngredient === null ||
        primaryIngredient === undefined ||
        primaryIngredient === ''
      ) {
        return '';
      }

      // Convert to number and back to string to remove trailing zeroes
      const numValue = parseFloat(primaryIngredient.toString());
      if (isNaN(numValue)) {
        return primaryIngredient.toString();
      }

      return numValue.toString();
    };

    const primaryIngredientTemplate = (rowData: DrugReferenceFgNdcToBulkNdc) => {
      return formatPrimaryIngredient(rowData.primary_ingredient);
    };

    if (isLoading && !drugReferenceData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.drugReferenceFgNdcToBulkNdcTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Drug Reference FG NDC to Bulk NDC Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                className={classes.searchInput}
                placeholder="Search..."
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>
            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />
            {/* {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )} */}
          </div>
        </div>

        <DataTable
          value={drugReferenceData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? drugReferenceData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100000}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="fg_ndc" header="FG NDC" />
          <Column field="bulk_ndc" header="Bulk NDC" />
          <Column field="description" header="Description" />
          <Column
            field="primary_ingredient"
            header="Primary Ingredient"
            body={primaryIngredientTemplate}
          />
          <Column field="supplier" header="Supplier" />
          <Column field="supplier_name" header="Supplier Name" />
          <Column field="dosage_form" header="Dosage Form" />
          <Column field="coating" header="Coating" body={coatingTemplate} />
          <Column field="product_color" header="Product Color" />
          {/* {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )} */}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalDrugReferenceFgNdcToBulkNdc
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
          coatingOptions={coatingOptions}
          dosageFormOptions={dosageFormOptions}
        />

        <DeleteConfirmationModalDrugReferenceFgNdcToBulkNdc
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default DrugReferenceFgNdcToBulkNdcTable;
